"use client";

import { useEffect, useRef } from "react";
import Icon from "@/components/ui/AppIcon";

const features = [
  {
    icon: "WrenchScrewdriverIcon",
    title: "Expert Assembly",
    description:
      "Every bike is professionally assembled and tuned by our certified mechanics before it reaches you. Ride-ready, guaranteed.",
    stat: "100%",
    statLabel: "Professionally tuned",
  },
  {
    icon: "ShieldCheckIcon",
    title: "3-Year Warranty",
    description:
      "We stand behind every bike we sell. Full frame warranty for 3 years, components covered for 1 year. No questions asked.",
    stat: "3yr",
    statLabel: "Full coverage",
    highlight: true,
  },
  {
    icon: "UserGroupIcon",
    title: "Ride Fit Guarantee",
    description:
      "Not the right fit? Return within 30 days for a full exchange. Our sizing experts help you find the perfect match before you buy.",
    stat: "30-day",
    statLabel: "Free returns",
  },
];

export default function WhyUsSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove("hidden");
          }
        });
      },
      { threshold: 0.1 }
    );
    const reveals = sectionRef.current?.querySelectorAll(".reveal");
    reveals?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="dark-section py-28 px-6 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-20 reveal hidden">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-amber mb-4">
            Why VelocityCycles
          </p>
          <h2 className="font-heading text-5xl md:text-7xl font-black leading-none tracking-tight">
            <span className="text-cream">Built for</span>{" "}
            <br />
            <span className="italic font-light text-amber">Real Riders.</span>
          </h2>
        </div>

        {/* Feature Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <div
              key={f.title}
              className={`feature-card rounded-4xl p-8 reveal hidden reveal-delay-${i + 1} ${
                f.highlight ? "border-amber/20" : ""
              }`}
            >
              {/* Icon */}
              <div
                className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-8 ${
                  f.highlight
                    ? "bg-amber text-charcoal" :"bg-white/5 text-amber border border-white/10"
                }`}
              >
                <Icon name={f.icon as any} size={24} />
              </div>

              {/* Stat */}
              <div className="mb-4">
                <span className="font-heading text-4xl font-black text-amber">{f.stat}</span>
                <p className="text-xs font-bold uppercase tracking-widest text-white/30 mt-1">
                  {f.statLabel}
                </p>
              </div>

              {/* Content */}
              <h3 className="font-heading text-xl font-bold text-cream mb-3">{f.title}</h3>
              <p className="text-white/50 text-sm leading-relaxed">{f.description}</p>

              {/* Bottom line */}
              <div className="mt-8 pt-6 border-t border-white/5 flex justify-between items-center">
                <span
                  className={`text-xs font-bold uppercase tracking-widest ${
                    f.highlight ? "text-amber" : "text-white/20"
                  }`}
                >
                  Learn More
                </span>
                <Icon
                  name="ArrowRightIcon"
                  size={16}
                  className={f.highlight ? "text-amber" : "text-white/20"}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}